import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { toPromoBrand } from '~/consts/promoBrand';

import { ListAllVoucherUsersQuery, listAllVoucherUsers, listVouchers } from '~/services/PromoService';

import { getVoucherToCreditBlockReason } from '~/utils/voucherToCredit';

import { voucherQueryKeys } from './useGetVouchersByIds';
import { allVoucherUsersQueryKeys } from './useListAllVoucherUsers';

// A customer rarely holds more than a handful of assignments of the same voucher.
const VOUCHER_USERS_LOOKUP_LIMIT = 100;

type Params = {
  voucherUserId: string;
  voucherId: string;
  userId: string;
  brand: string;
};

/**
 * Returns a function that re-fetches the voucher and the assignment, bypassing the cache,
 * and resolves to why the assignment cannot be converted to credit, or null when it still can.
 * Run it right before granting credit: the list the agent clicked from may be out of date.
 */
export function useCheckVoucherToCredit({ voucherUserId, voucherId, userId, brand }: Params) {
  const queryClient = useQueryClient();

  return useCallback(async (): Promise<string | null> => {
    const promoBrand = toPromoBrand(brand) ?? 'luxuryescapes';
    const voucherUsersQuery: ListAllVoucherUsersQuery = {
      voucherId,
      userId,
      brand: promoBrand,
      includeExpired: true,
      includeUsed: true,
      limit: VOUCHER_USERS_LOOKUP_LIMIT,
    };

    const [voucherUsersResponse, vouchersResponse] = await Promise.all([
      queryClient.fetchQuery({
        queryKey: allVoucherUsersQueryKeys.list(voucherUsersQuery),
        queryFn: ({ signal }) => listAllVoucherUsers(voucherUsersQuery, { signal }),
        staleTime: 0,
      }),
      queryClient.fetchQuery({
        queryKey: [...voucherQueryKeys.byIds([voucherId]), 'fresh', promoBrand],
        queryFn: () => listVouchers({ voucher_ids: [voucherId], brand: promoBrand, limit: 1 }),
        staleTime: 0,
      }),
    ]);

    return getVoucherToCreditBlockReason({
      voucherUser: voucherUsersResponse.result?.voucherUsers?.find((row) => row.voucherUserId === voucherUserId),
      voucher: vouchersResponse.result?.vouchers?.find((voucher) => voucher.voucherId === voucherId),
    });
  }, [queryClient, voucherUserId, voucherId, userId, brand]);
}
